import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { PlayCircle, X, Sparkles, ArrowRight, CheckCircle } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

export const WelcomeOverlay = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [showVideo, setShowVideo] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    // Welcome-Overlay NUR für nicht-angemeldete Nutzer
    if (user) {
      return; // Kein Overlay für angemeldete Nutzer
    }
    
    // Prüfe ob das Overlay bereits gezeigt wurde (für nicht-angemeldete Nutzer)
    const hasSeenWelcome = localStorage.getItem('welcome_shown_anonymous');
    
    if (!hasSeenWelcome) {
      const timer = setTimeout(() => {
        setIsOpen(true);
      }, 3000); // 3 Sekunden Verzögerung für nicht-angemeldete Nutzer
      
      return () => clearTimeout(timer);
    }
  }, [user]);

  const handleClose = () => {
    setIsOpen(false);
    // Für nicht-angemeldete Nutzer speichern
    localStorage.setItem('welcome_shown_anonymous', 'true');
  };

  const handleWatchTutorial = () => {
    setShowVideo(true);
    // Für nicht-angemeldete Nutzer speichern
    localStorage.setItem('welcome_shown_anonymous', 'true');
  };

  const handleSkip = () => {
    handleClose();
  };

  if (user) return null; // Kein Overlay für angemeldete Nutzer

  return (
    <>
      {/* Willkommens-Overlay */}
      <Dialog open={isOpen && !showVideo} onOpenChange={handleClose}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <div className="text-center space-y-4">
              <div className="mx-auto w-16 h-16 bg-gradient-to-br from-emerald-400 to-emerald-600 rounded-full flex items-center justify-center">
                <Sparkles className="w-8 h-8 text-white" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">
                  Willkommen bei KaufPause! 🎉
                </h2>
                <p className="text-gray-600">
                  Schön, dass du da bist! Lass uns dir zeigen, wie du mit KaufPause 
                  bewusster konsumieren und Geld sparen kannst.
                </p>
              </div>
            </div>
          </DialogHeader>
          
          <div className="space-y-4 mt-6">
            <Card className="border-emerald-200 bg-emerald-50">
              <CardContent className="pt-4">
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0" />
                    <span className="text-sm text-emerald-800">30-Tage-Wartezeit für bewusste Entscheidungen</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0" />
                    <span className="text-sm text-emerald-800">Impulskäufe vermeiden und Geld sparen</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0" />
                    <span className="text-sm text-emerald-800">Persönliche Sparziele erreichen</span>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <div className="flex flex-col gap-3">
              <Button 
                onClick={handleWatchTutorial}
                className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white"
              >
                <PlayCircle className="w-4 h-4 mr-2" />
                Tutorial ansehen (2 Min)
              </Button>
              
              <Button 
                variant="outline" 
                onClick={handleSkip}
                className="w-full border-gray-300 text-gray-700 hover:bg-gray-50"
              >
                Später ansehen
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Tutorial-Video Overlay */}
      <Dialog open={showVideo} onOpenChange={() => setShowVideo(false)}>
        <DialogContent className="sm:max-w-4xl">
          <DialogHeader className="text-lg font-semibold">
            KaufPause Tutorial - So nutzt du die App optimal
            <button
              onClick={() => setShowVideo(false)}
              className="absolute right-4 top-4 text-gray-400 hover:text-gray-600"
            >
              <X className="h-4 w-4" />
              <span className="sr-only">Schließen</span>
            </button>
          </DialogHeader>
          <div className="aspect-video w-full overflow-hidden rounded-md">
            <iframe
              width="100%"
              height="100%"
              src="https://www.youtube-nocookie.com/embed/_53vpTe-JOA?autoplay=1"
              title="KaufPause Tutorial - So nutzt du die App optimal"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              className="rounded-md"
            ></iframe>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}; 